/**
 * VoucherDrawer.tsx — right-side detail pane for a single Voucher.
 *
 * Fired from VoucherRegister row click or Enter keypress; closed on Esc.
 * Renders header (number / type / status / date / narration) + VoucherLines
 * table + multi-currency triplet + audit footer + status-aware actions.
 *
 * Strictly READ-ONLY (status indicator: "Locked by VoucherLine immutability"
 * when posted/cancelled/amended). All mutation flows are server-side.
 */

import React, { useEffect } from 'react';
import { X, Lock, ExternalLink, FileText, RotateCw, AlertTriangle, ArrowLeftRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatINR, formatDate } from '@/utils/formatters';
import TypeChip from './TypeChip';
import { useVoucher, type Voucher, type VoucherLine } from '@/hooks/useVouchers';

// ─── Status badge ───────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; text: string; border: string; label: string }> = {
  draft: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
    label: 'Draft',
  },
  posted: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    border: 'border-emerald-300',
    label: 'Posted',
  },
  cancelled: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    label: 'Cancelled',
  },
  amended: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-300',
    label: 'Amended',
  },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.draft!;
  const strike = status === 'cancelled' ? 'line-through' : '';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${s.bg} ${s.text} ${s.border} ${strike}`}
    >
      {s.label}
    </span>
  );
}

const LOCKED_STATUSES = new Set(['posted', 'cancelled', 'amended']);

// ─── Reference-doctype → internal route resolver ────────────────────────────

function refRoute(referenceType?: string | null, referenceName?: string | null): string | null {
  if (!referenceType || !referenceName) return null;
  const map: Record<string, string> = {
    SalesInvoice: `/invoices/${referenceName}`,
    PurchaseInvoice: `/purchase-invoices/${referenceName}`,
    SalesOrder: `/sales-orders/${referenceName}`,
    PurchaseOrder: `/purchase-orders/${referenceName}`,
    Payment: `/payments/${referenceName}`,
    Voucher: `/ledger/vouchers/${referenceName}`,
    Asset: `/assets/${referenceName}`,
    ExpenseClaim: `/expenses/${referenceName}`,
    PayrollEntry: `/hr/payroll/${referenceName}`,
    EmployeeAdvance: `/hr/advances/${referenceName}`,
    Loan: `/finance/loans/${referenceName}`,
    BankTransaction: `/finance/bank/${referenceName}`,
    BillOfEntry: `/imports/boe/${referenceName}`,
    Fees: `/fees/${referenceName}`,
    InvoiceDiscounting: `/finance/discounting/${referenceName}`,
    FullAndFinalStatement: `/hr/fnf/${referenceName}`,
    ExchangeRateRevaluation: `/finance/fx-reval/${referenceName}`,
  };
  return map[referenceType] ?? null;
}

// ─── Drawer ─────────────────────────────────────────────────────────────────

interface VoucherDrawerProps {
  voucherId: string | null;
  /** Optional voucher header passed in from the list — drawer still fetches the full
   *  detail (with lines) but uses the header as instant skeleton content. */
  preview?: Voucher | null;
  onClose: () => void;
}

const VoucherDrawer: React.FC<VoucherDrawerProps> = ({ voucherId, preview, onClose }) => {
  const { voucher: fetched, loading, error } = useVoucher(voucherId);
  const voucher = fetched ?? preview ?? null;

  // Esc to close. Stable handler — only depends on `onClose`.
  useEffect(() => {
    if (!voucherId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [voucherId, onClose]);

  if (!voucherId) return null;

  const status = (voucher?.status ?? '').toLowerCase();
  const isLocked = LOCKED_STATUSES.has(status);
  const lines: VoucherLine[] = voucher?.lines ?? [];
  const isMultiCurrency = !!voucher?.multiCurrency;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 print:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <aside
        data-testid="voucher-drawer"
        className="fixed right-0 top-0 z-50 h-full w-full max-w-3xl overflow-y-auto border-l border-gray-200 bg-white shadow-xl print:static print:max-w-none print:border-0 print:shadow-none"
        role="dialog"
        aria-label="Voucher detail"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4 print:static">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  {voucher?.number ?? '—'}
                </h2>
                {voucher?.type ? <TypeChip type={voucher.type} size="md" /> : null}
                {voucher?.status ? <StatusBadge status={status} /> : null}
                {isLocked ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-500"
                    title="Locked by VoucherLine immutability"
                  >
                    <Lock className="h-3 w-3" />
                    Locked
                  </span>
                ) : null}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                <span>{voucher?.date ? formatDate(voucher.date) : '—'}</span>
                {voucher?.narration ? <span className="truncate">{voucher.narration}</span> : null}
              </div>
            </div>
            <button
              type="button"
              data-testid="voucher-drawer-close-button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 print:hidden"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Cancelled banner */}
        {status === 'cancelled' ? (
          <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                Voucher cancelled
                {voucher?.cancelledAt ? ` on ${formatDate(voucher.cancelledAt)}` : ''}
                {voucher?.cancelledBy ? ` by ${voucher.cancelledBy}` : ''}.
                {voucher?.reversalOf ? (
                  <>
                    {' '}
                    See amendment chain — original voucher{' '}
                    <Link
                      to={`/ledger/vouchers/${voucher.reversalOf}`}
                      className="font-medium underline"
                    >
                      {voucher.reversalOf}
                    </Link>
                    .
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* Body */}
        <div className="px-6 py-4">
          {loading && !voucher ? (
            <div className="py-10 text-center text-sm text-gray-400">Loading voucher…</div>
          ) : error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          ) : (
            <>
              {/* Top metadata grid */}
              <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs sm:grid-cols-3">
                <Meta label="Reference" value={voucher?.reference} />
                <Meta label="Fiscal Year" value={voucher?.fiscalYear} />
                <Meta label="Mode of Payment" value={voucher?.modeOfPayment} />
                {voucher?.chequeNo ? <Meta label="Cheque No" value={voucher.chequeNo} /> : null}
                {voucher?.chequeDate ? (
                  <Meta label="Cheque Date" value={formatDate(voucher.chequeDate)} />
                ) : null}
                {voucher?.clearanceDate ? (
                  <Meta label="Clearance Date" value={formatDate(voucher.clearanceDate)} />
                ) : null}
                {isMultiCurrency ? (
                  <div className="col-span-full inline-flex items-center gap-1 rounded border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] font-medium text-violet-800">
                    <ArrowLeftRight className="h-3 w-3" /> Multi-currency voucher
                  </div>
                ) : null}
              </div>

              {/* Lines table */}
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-sm" data-testid="voucher-lines-table">
                  <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Account</th>
                      <th className="px-3 py-2">Party</th>
                      <th className="px-3 py-2 text-right">Debit</th>
                      <th className="px-3 py-2 text-right">Credit</th>
                      <th className="px-3 py-2">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-gray-400">
                          No lines loaded.
                        </td>
                      </tr>
                    ) : (
                      lines.map((l, idx) => {
                        const route = refRoute(l.referenceType, l.referenceName);
                        const debit = Number(l.debit) || 0;
                        const credit = Number(l.credit) || 0;
                        const xRate = l.transactionExchangeRate
                          ? Number(l.transactionExchangeRate)
                          : null;
                        const showFx =
                          isMultiCurrency &&
                          xRate !== null &&
                          (l.transactionCurrency || l.accountCurrency);
                        return (
                          <React.Fragment key={l.id ?? idx}>
                            <tr className="border-t border-gray-100 align-top">
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">
                                  {l.ledgerName ?? l.ledger?.name ?? l.ledgerId}
                                </div>
                                {l.ledger?.code ? (
                                  <div className="text-[11px] text-gray-400">{l.ledger.code}</div>
                                ) : null}
                                {l.isTaxWithholdingAccount ? (
                                  <span className="mt-1 inline-flex rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                                    TDS
                                  </span>
                                ) : null}
                                {l.narration ? (
                                  <div className="mt-1 text-[11px] text-gray-500">
                                    {l.narration}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {l.partyType ? (
                                  <div className="text-[11px] uppercase text-gray-400">
                                    {l.partyType}
                                  </div>
                                ) : null}
                                <div className="text-xs">{l.partyId ?? '—'}</div>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                                {debit > 0 ? formatINR(debit) : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                                {credit > 0 ? formatINR(credit) : '—'}
                              </td>
                              <td className="px-3 py-2 text-xs">
                                {l.referenceType ? (
                                  route ? (
                                    <Link
                                      to={route}
                                      className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                      {l.referenceType}: {l.referenceName}
                                    </Link>
                                  ) : (
                                    <span className="text-gray-600">
                                      {l.referenceType}: {l.referenceName ?? '—'}
                                    </span>
                                  )
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                            {showFx ? (
                              <tr className="bg-violet-50/50 text-[11px] text-violet-900">
                                <td colSpan={5} className="px-3 py-1">
                                  <span className="font-medium">FX:</span>{' '}
                                  {l.transactionCurrency ?? '—'}{' '}
                                  {debit > 0 ? Number(l.debitInAccountCurrency) || 0 : Number(l.creditInAccountCurrency) || 0}
                                  {' × '}
                                  {xRate}
                                  {' = '}
                                  ₹{formatINR(debit > 0 ? debit : credit).replace('₹', '')}
                                  {l.accountCurrency ? (
                                    <span className="ml-2 text-violet-700">
                                      (account: {l.accountCurrency})
                                    </span>
                                  ) : null}
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50 text-sm font-semibold">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>
                        Total
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatINR(Number(voucher?.totalDebit) || 0)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatINR(Number(voucher?.totalCredit) || 0)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Actions row — status-aware, READ-ONLY */}
              <div className="mt-4 flex flex-wrap items-center gap-2 print:hidden">
                {voucher?.reversalOf ? (
                  <Link
                    to={`/ledger/vouchers/${voucher.reversalOf}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <RotateCw className="h-3.5 w-3.5" />
                    View original
                  </Link>
                ) : null}
                {voucher?.amendedFrom ? (
                  <Link
                    to={`/ledger/vouchers/${voucher.amendedFrom}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    View amendment chain
                  </Link>
                ) : null}
                <button
                  type="button"
                  data-testid="voucher-drawer-print-button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Print
                </button>
              </div>

              {/* Audit footer */}
              <div className="mt-6 border-t border-gray-100 pt-4 text-xs text-gray-500">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-4">
                  <Meta
                    small
                    label="Created"
                    value={voucher?.createdAt ? formatDate(voucher.createdAt) : null}
                  />
                  <Meta small label="Created by" value={voucher?.createdBy} />
                  <Meta
                    small
                    label="Posted"
                    value={voucher?.postedAt ? formatDate(voucher.postedAt) : null}
                  />
                  <Meta small label="Posted by" value={voucher?.postedBy} />
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

function Meta({
  label,
  value,
  small = false,
}: {
  label: string;
  value?: string | number | null;
  small?: boolean;
}) {
  return (
    <div>
      <div className={`uppercase tracking-wider ${small ? 'text-[10px]' : 'text-[10px]'} text-gray-400`}>
        {label}
      </div>
      <div className={small ? 'text-xs text-gray-700' : 'text-sm text-gray-800'}>
        {value ?? '—'}
      </div>
    </div>
  );
}

export default VoucherDrawer;
